import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { toCamelCase } from '@/lib/typeHelper'
import type { Broker } from '@/models/broker'
import type { InsuranceIndication } from '@/models/insuranceIndication'
import { InsuranceIndicationStatus } from '@/models/insuranceTypes'

/**
 * A quotes page shared by link. Deliberately narrower than the authed application
 * payload — see PublicQuotesSerializer — so a forwarded link shows carrier prices
 * rather than the customer's fleet, drivers or documents.
 */
export interface PublicQuotes {
  id: string
  businessName: string
  /** The broker to contact about these quotes; null when nobody is assigned. */
  broker: Broker | null
  policyExpirationDate: string | null
  selectedCoverages: string[]
  indications: InsuranceIndication[]
  /** Carriers already asked for a price whose indication rows don't exist yet. */
  awaitingCarrierCount: number
}

const POLL_MS = 5000

/** The read-only share link for an application's quotes; anyone holding it can read them. */
export function publicQuotesUrl(applicationId: string): string {
  return `${window.location.origin}/quotes/${applicationId}`
}

/** Whether anything on the page can still change — placeholders, or carriers still working. */
function hasWorkInFlight(quotes: PublicQuotes | undefined): boolean {
  if (!quotes) return false
  if (quotes.awaitingCarrierCount > 0) return true
  return quotes.indications.some((indication) => indication.status === InsuranceIndicationStatus.PENDING)
}

export function usePublicQuotes(applicationId: string | undefined) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['PUBLIC_QUOTES', applicationId],
    queryFn: () =>
      axios
        .get(`/api/v1/insure/public/quotes/${applicationId}/`)
        .then((res) => toCamelCase(res.data) as PublicQuotes),
    enabled: !!applicationId,
    retry: false,
    // Nothing else refetches this page, so unresolved cards would sit there until a reload.
    refetchInterval: (query) => (hasWorkInFlight(query.state.data) ? POLL_MS : false),
  })
  return { quotes: data, isLoading, isError }
}
